<template>
    <div class="manage-access">
        <h2>Manage access</h2>
        <p>Grant other users access to view your clients, analyses and reports.</p>

        <div class="grant-section">
            <h3 class="section-title">Add a user</h3>
            <div class="grant-form">
                <el-input
                    v-model="emailInput"
                    placeholder="Enter user's email address"
                    @keyup.enter="findUser"
                    :disabled="searching"
                    size="large">
                    <template #prefix>
                        <el-icon><Search /></el-icon>
                    </template>
                </el-input>
                <el-button type="primary" @click="findUser" :loading="searching" size="large">
                    Find user
                </el-button>
            </div>

            <div v-if="searchError" class="search-error">
                {{ searchError }}
            </div>

            <div v-if="foundUser" class="found-user-row">
                <div class="found-user">
                    <div class="found-avatar">{{ (foundUser.givenName?.charAt(0) || '') + (foundUser.surname?.charAt(0) || '') }}</div>
                    <div>
                        <div class="found-name">{{ foundUser.givenName }} {{ foundUser.surname }}</div>
                        <div class="found-email">{{ emailInput }}</div>
                    </div>
                </div>
                <el-button type="success" @click="grantAccess" :loading="granting" size="large">
                    Grant access
                </el-button>
            </div>
        </div>

        <div class="grants-section">
            <h3 class="section-title">Users with access to your data</h3>
            <div v-if="loadingGrants" class="grants-loading">Loading, please wait…</div>
            <div v-else-if="grants.length === 0" class="grants-empty">
                No users have been granted access yet.
            </div>
            <div v-else class="grants-list">
                <div v-for="grant in grants" :key="grant.viewerId" class="grant-row">
                    <div class="grant-avatar">{{ (grant.viewerName?.charAt(0) || '') + (grant.viewerName?.split(' ')[1]?.charAt(0) || '') }}</div>
                    <div class="grant-info">
                        <div class="grant-name">{{ grant.viewerName }}</div>
                        <div class="grant-email">{{ grant.viewerEmail }}</div>
                    </div>
                    <div class="grant-date">Granted: {{ formatDate(grant.grantedAt) }}</div>
                    <el-button type="danger" text @click="revokeAccess(grant.viewerId)" size="small">
                        Revoke
                    </el-button>
                </div>
            </div>
        </div>

        <br>
        <div style="display: flex; justify-content: center;">
            <el-button type="primary" @click="goHome()"> Continue to ProtectionGuruPro </el-button>
        </div>
    </div>
</template>

<style scoped>
.manage-access {
    max-width: 1100px;
    min-width: 1044px;
    width: 100%;
    margin: 24px auto;
    background: white;
    border-radius: 8px;
    padding: 24px;
}

.manage-access h2 {
    color: #1e228c;
    margin: 0 0 8px 0;
}

.manage-access p {
    color: #666;
    margin: 0 0 24px 0;
}

.section-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin: 0 0 12px 0;
}

.grant-form {
    display: flex;
    gap: 10px;
    max-width: 600px;
}

.grant-form .el-input {
    width: 350px;
    flex-shrink: 0;
}

.search-error {
    color: #e74c3c;
    font-size: 13px;
    margin-top: 8px;
}

.found-user-row {
    display: flex;
    gap: 10px;
    max-width: 600px;
    margin-top: 12px;
}

.found-user {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 8px 16px;
    width: 350px;
    flex-shrink: 0;
    box-sizing: border-box;
}

.found-avatar, .grant-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #353C8E;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 12px;
    flex-shrink: 0;
}

.found-name, .grant-name {
    font-weight: 600;
    font-size: 14px;
    color: #1a1a2e;
}

.found-email, .grant-email {
    font-size: 12px;
    color: #888;
}

.grants-section {
    margin-top: 32px;
}

.grants-loading, .grants-empty {
    color: #888;
    font-size: 13px;
    padding: 8px 0;
}

.grants-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.grant-row {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #F3F2EF;
    border-radius: 8px;
    padding: 10px 16px;
}

.grant-info {
    flex: 1;
}

.grant-date {
    font-size: 12px;
    color: #888;
}
</style>

<script>
export default {
    name: 'ManageAccess',
    inject: ['UserService'],
    data() {
        return {
            emailInput: '',
            searching: false,
            granting: false,
            loadingGrants: false,
            searchError: null,
            foundUser: null,
            grants: []
        }
    },
    mounted() {
        this.loadGrants()
    },
    methods: {
        async findUser() {
            if (!this.emailInput.trim()) return

            this.searching = true
            this.searchError = null
            this.foundUser = null

            try {
                const response = await fetch('/api/Access/FindUser?email=' + encodeURIComponent(this.emailInput.trim()))
                if (response.ok) {
                    var user = await response.json()
                    var currentUser = this.UserService.getUserDetails()
                    if (currentUser && user.userId === currentUser.userIdentifier) {
                        this.searchError = 'You cannot grant access to yourself.'
                    } else if (this.grants.some(g => g.viewerId === user.userId)) {
                        this.searchError = 'This user already has access to your data.'
                    } else {
                        this.foundUser = user
                    }
                } else if (response.status === 404) {
                    this.searchError = 'No user found with that email address.'
                } else {
                    this.searchError = 'An error occurred while searching.'
                }
            } catch (e) {
                this.searchError = 'An error occurred while searching.'
            } finally {
                this.searching = false
            }
        },

        async grantAccess() {
            if (!this.foundUser) return

            this.granting = true
            try {
                const response = await fetch('/api/Access/Grant', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        targetUserId: this.foundUser.userId,
                        targetName: this.foundUser.givenName + ' ' + this.foundUser.surname,
                        targetEmail: this.emailInput.trim()
                    })
                })

                if (response.ok) {
                    this.foundUser = null
                    this.emailInput = ''
                    this.searchError = null
                    await this.loadGrants()
                } else {
                    var errorText = await response.text()
                    this.searchError = errorText || 'Failed to grant access.'
                }
            } catch (e) {
                this.searchError = 'An error occurred while granting access.'
            } finally {
                this.granting = false
            }
        },

        async revokeAccess(viewerId) {
            try {
                const response = await fetch('/api/Access/Revoke/' + viewerId, { method: 'DELETE' })
                if (response.ok) {
                    await this.loadGrants()
                }
            } catch (e) {
                console.error('Failed to revoke access:', e)
            }
        },

        goHome() {
            window.location.href = '/'
        },

        async loadGrants() {
            this.loadingGrants = true
            try {
                const response = await fetch('/api/Access/MyGrants')
                if (response.ok) {
                    this.grants = await response.json()
                }
            } catch (e) {
                console.error('Failed to load grants:', e)
            } finally {
                this.loadingGrants = false
            }
        },

        formatDate(dateStr) {
            if (!dateStr) return ''
            var d = new Date(dateStr)
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
        }
    }
}
</script>
